import { useEffect, useState } from 'react';
import { HTTPError } from 'ky';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { ApprovalSection, StatusBadge, formatDateTime } from '@/components/features/approval/approval-common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { useApi } from '@/hooks/useApi';
import { approvalAdminClient } from '@/lib/approval-client';
import type {
  ApprovalProgramSetting,
  Delegation,
  JobTitle,
  SearchScope,
  RuleConditionLine,
  RuleSetSaveRequest,
  RuleStep,
} from '@/types/approval';

/**
 * 전자결재 관리(approval-admin). 프로그램 사용설정·전결규정·위임·직책코드·비활성 결재자를 한 화면의 탭으로 묶는다.
 * 관리 API 는 `approvals/admin/**` 이고 유효성(중복 조건, 미지원 필드, 비활성 사용자, 배정 이력 존재)은 서버가 400 으로 막는다.
 */

const PROGRAM_LABEL: Record<string, string> = {
  STORE_PRICE: '주유소 판매단가',
  DEALER_PRICE: '거래처 계약단가',
};

const APPLY_METHOD_LABEL: Record<string, string> = {
  IMMEDIATE: '즉시 반영',
  EVENT_TRIGGERED: '마감 시 반영',
};

const RULE_STATUS_LABEL: Record<string, string> = {
  DRAFT: '작성중',
  ACTIVE: '적용',
  EXPIRED: '만료',
};

/** 조건 필드는 프로그램마다 다르다. 목록에 없는 필드는 서버가 400 으로 거른다. */
const CONDITION_FIELDS: Record<string, { code: string; name: string }[]> = {
  STORE_PRICE: [
    { code: 'VARIANCE_RATE', name: '변동률 (%)' },
    { code: 'PRICE_GAP_AMT', name: '단가 차액' },
    { code: 'APPLY_TYPE', name: '적용구분' },
  ],
  // 거래처 계약단가는 단가적용방법마다 단위가 달라 단가 차액 조건이 없다
  DEALER_PRICE: [{ code: 'OIL_SALE_TYPE', name: '판매구분' }],
};

const OPERATORS = ['=', '<', '<=', '>', '>=', 'IN', 'BETWEEN'];

const programLabel = (programId: string | null) =>
  programId ? PROGRAM_LABEL[programId] ?? programId : '전체';

const numOrNull = (value: string) => (value.trim() === '' ? null : Number(value));
const textOrNull = (value: string) => (value.trim() === '' ? null : value.trim());

/**
 * api-client 의 beforeError 가 4xx 응답 본문 메시지를 error.message 로 갈아끼운다.
 * 5xx 는 ky 기본 영문 문구라 그대로 노출하지 않고 폴백 문구를 쓴다.
 */
function errorToast(title: string, error: unknown, fallback: string) {
  const status = error instanceof HTTPError ? error.response.status : 0;
  const serverMessage = status >= 400 && status < 500 && error instanceof Error ? error.message : '';
  toast({ title, description: serverMessage || fallback, variant: 'destructive' });
}

function LoadingRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: 4 }).map((_, row) => (
        <TableRow key={row}>
          {Array.from({ length: cols }).map((_, col) => (
            <TableCell key={col}>
              <Skeleton className='h-4 w-20' />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyRow({ cols, message }: { cols: number; message: string }) {
  return (
    <TableRow>
      <TableCell colSpan={cols} className='py-12 text-center text-muted-foreground'>
        {message}
      </TableCell>
    </TableRow>
  );
}

export function ApprovalAdminScreen() {
  return (
    <div className='flex flex-col gap-3'>
      <h1 className='min-w-0 text-base font-semibold leading-tight'>전자결재 관리</h1>
      <ApprovalSection>
        <Tabs defaultValue='programs' className='p-3'>
        <TabsList className='mb-2'>
          <TabsTrigger value='programs'>프로그램</TabsTrigger>
          <TabsTrigger value='rule-sets'>전결규정</TabsTrigger>
          <TabsTrigger value='delegations'>위임</TabsTrigger>
          <TabsTrigger value='job-titles'>직책 코드</TabsTrigger>
          <TabsTrigger value='pending-approvers'>비활성 결재자</TabsTrigger>
        </TabsList>

        <TabsContent value='programs'>
          <ProgramsTab />
        </TabsContent>
        <TabsContent value='rule-sets'>
          <RuleSetsTab />
        </TabsContent>
        <TabsContent value='delegations'>
          <DelegationsTab />
        </TabsContent>
        <TabsContent value='job-titles'>
          <JobTitlesTab />
        </TabsContent>
        <TabsContent value='pending-approvers'>
          <PendingApproversTab />
        </TabsContent>
        </Tabs>
      </ApprovalSection>
    </div>
  );
}

// ---------------------------------------------------------------- 1. 프로그램

function ProgramsTab() {
  const { data, loading, refetch } = useApi(() => approvalAdminClient.programs(), []);
  const [editing, setEditing] = useState<ApprovalProgramSetting | null>(null);
  const programs = data ?? [];

  return (
    <div className='space-y-4'>
      <div className='rounded-lg border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>프로그램명</TableHead>
              <TableHead className='w-28'>반영방식</TableHead>
              <TableHead className='w-20'>사용여부</TableHead>
              <TableHead>적용 전결규정</TableHead>
              <TableHead className='w-32'>리마인더</TableHead>
              <TableHead className='w-32'>적용대기 경고</TableHead>
              <TableHead className='w-20 text-right'>작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <LoadingRows cols={7} />
            ) : programs.length === 0 ? (
              <EmptyRow cols={7} message='등록된 결재 프로그램이 없습니다' />
            ) : (
              programs.map(program => (
                <TableRow key={program.programId}>
                  <TableCell className='font-medium'>{program.name}</TableCell>
                  <TableCell>{APPLY_METHOD_LABEL[program.applyMethod] ?? program.applyMethod}</TableCell>
                  <TableCell>
                    {program.isUsed ? <Badge variant='outline'>사용</Badge> : <Badge variant='secondary'>미사용</Badge>}
                  </TableCell>
                  <TableCell className='text-muted-foreground'>{program.activeRuleSetName ?? '-'}</TableCell>
                  <TableCell>
                    {program.reminderHours == null ? '-' : `${program.reminderHours}시간 / ${program.reminderMaxCount ?? 0}회`}
                  </TableCell>
                  <TableCell>{program.pendingAlertHours == null ? '-' : `${program.pendingAlertHours}시간`}</TableCell>
                  <TableCell className='text-right'>
                    <Button variant='ghost' size='sm' className='h-8 w-8 p-0' onClick={() => setEditing(program)}>
                      <Pencil className='h-4 w-4' />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <ProgramUseDialog
          program={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function ProgramUseDialog({
  program,
  onClose,
  onSaved,
}: {
  program: ApprovalProgramSetting;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: ruleSets, loading } = useApi(
    () => approvalAdminClient.ruleSets(program.programId),
    [program.programId]
  );
  const activeRuleSets = (ruleSets ?? []).filter(ruleSet => ruleSet.status === 'ACTIVE');

  const [isUsed, setIsUsed] = useState(program.isUsed ?? false);
  // Radix SelectItem 은 빈 문자열 value 를 금지한다. '미지정'은 센티널로 두고 저장 시 null 로 바꾼다.
  const [ruleSetId, setRuleSetId] = useState(program.activeRuleSetId == null ? 'none' : String(program.activeRuleSetId));
  const [reminderHours, setReminderHours] = useState(program.reminderHours?.toString() ?? '');
  const [reminderMaxCount, setReminderMaxCount] = useState(program.reminderMaxCount?.toString() ?? '');
  const [pendingAlertHours, setPendingAlertHours] = useState(program.pendingAlertHours?.toString() ?? '');
  const [startDate, setStartDate] = useState(program.startDate ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await approvalAdminClient.saveProgramUse(program.programId, {
        isUsed,
        activeRuleSetId: ruleSetId === 'none' ? null : Number(ruleSetId),
        reminderHours: numOrNull(reminderHours),
        reminderMaxCount: numOrNull(reminderMaxCount),
        pendingAlertHours: numOrNull(pendingAlertHours),
        startDate: textOrNull(startDate),
      });
      toast({ title: '저장 완료', description: `${program.name} 설정을 저장했습니다` });
      onSaved();
    } catch (error) {
      // 적용 중인 전결규정 없이 사용을 켜면 서버가 400 으로 막는다
      errorToast('저장 실패', error, '프로그램 설정을 저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle>{program.name} 사용설정</DialogTitle>
          <DialogDescription>
            사용을 켜려면 적용(ACTIVE) 상태인 전결규정을 하나 지정해야 합니다.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-3 py-2'>
          <div className='flex items-center justify-between rounded-lg border p-3'>
            <Label htmlFor='program-used'>결재 사용</Label>
            <Switch id='program-used' checked={isUsed} onCheckedChange={setIsUsed} />
          </div>

          <div className='space-y-1'>
            <Label>적용 전결규정</Label>
            <Select value={ruleSetId} onValueChange={setRuleSetId} disabled={loading}>
              <SelectTrigger>
                <SelectValue placeholder={loading ? '불러오는 중...' : '전결규정 선택'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>지정 안 함</SelectItem>
                {activeRuleSets.map(ruleSet => (
                  <SelectItem key={ruleSet.id} value={String(ruleSet.id)}>
                    {ruleSet.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loading && activeRuleSets.length === 0 && (
              <p className='text-sm text-muted-foreground'>적용 상태인 전결규정이 없습니다. 전결규정 탭에서 먼저 등록하세요.</p>
            )}
          </div>

          <div className='grid grid-cols-3 gap-3'>
            <div className='space-y-1'>
              <Label htmlFor='program-reminder-hours'>리마인더(시간)</Label>
              <Input
                id='program-reminder-hours'
                type='number'
                value={reminderHours}
                onChange={e => setReminderHours(e.target.value)}
                placeholder='미사용'
              />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='program-reminder-count'>리마인더(횟수)</Label>
              <Input
                id='program-reminder-count'
                type='number'
                value={reminderMaxCount}
                onChange={e => setReminderMaxCount(e.target.value)}
                placeholder='미사용'
              />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='program-pending-hours'>적용대기 경고(시간)</Label>
              <Input
                id='program-pending-hours'
                type='number'
                value={pendingAlertHours}
                onChange={e => setPendingAlertHours(e.target.value)}
                placeholder='미사용'
              />
            </div>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='program-start-date'>시작일</Label>
            <Input
              id='program-start-date'
              type='date'
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- 2. 전결규정

type EditableCondition = RuleSetSaveRequest['conditions'][number];
type EditableStep = Omit<RuleStep, 'userName' | 'roleJobTitleName'>;

const newLine = (programId: string, conditionGroup: number, lineOrder: number): RuleConditionLine => ({
  conditionGroup,
  lineOrder,
  conditionField: CONDITION_FIELDS[programId]?.[0]?.code ?? '',
  conditionOperator: '>=',
  conditionValue1: '',
  conditionValue2: null,
});

const newStep = (stepOrder: number): EditableStep => ({
  stepOrder,
  stepAssignMode: 'FIXED',
  userId: null,
  roleJobTitleCode: null,
  stepLabel: null,
  isFinalDelegate: false,
  isMandatory: true,
});

const newCondition = (sortOrder: number): EditableCondition => ({
  sortOrder,
  isExecutiveRequired: false,
  lines: [],
  steps: [newStep(1)],
});

function RuleSetsTab() {
  const { data, loading, refetch } = useApi(() => approvalAdminClient.ruleSets(), []);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const ruleSets = data ?? [];

  const openEditor = (id: number | null) => {
    setEditingId(id);
    setEditorOpen(true);
  };

  return (
    <div className='space-y-4'>
      <div className='flex justify-end'>
        <Button onClick={() => openEditor(null)}>
          <Plus className='mr-2 h-4 w-4' />새 규정
        </Button>
      </div>

      <div className='rounded-lg border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-40'>프로그램</TableHead>
              <TableHead>규정명</TableHead>
              <TableHead className='w-24'>상태</TableHead>
              <TableHead className='w-56'>기간</TableHead>
              <TableHead className='w-24'>적용중</TableHead>
              <TableHead className='w-40'>등록일시</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <LoadingRows cols={6} />
            ) : ruleSets.length === 0 ? (
              <EmptyRow cols={6} message='등록된 전결규정이 없습니다' />
            ) : (
              ruleSets.map(ruleSet => (
                <TableRow
                  key={ruleSet.id}
                  className='cursor-pointer transition-colors hover:bg-muted/50'
                  onClick={() => openEditor(ruleSet.id)}
                >
                  <TableCell>{programLabel(ruleSet.programId)}</TableCell>
                  <TableCell className='font-medium'>{ruleSet.name}</TableCell>
                  <TableCell>{RULE_STATUS_LABEL[ruleSet.status] ?? ruleSet.status}</TableCell>
                  <TableCell className='text-muted-foreground'>
                    {(ruleSet.startDate ?? '시작일 없음') + ' ~ ' + (ruleSet.endDate ?? '종료일 없음')}
                  </TableCell>
                  <TableCell>{ruleSet.active && <Badge variant='outline'>적용중</Badge>}</TableCell>
                  <TableCell className='text-muted-foreground'>{formatDateTime(ruleSet.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editorOpen && (
        <RuleSetDialog
          ruleSetId={editingId}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            setEditorOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function RuleSetDialog({
  ruleSetId,
  onClose,
  onSaved,
}: {
  ruleSetId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: jobTitles } = useApi(() => approvalAdminClient.jobTitles(), []);
  const [form, setForm] = useState<RuleSetSaveRequest>({
    programId: 'STORE_PRICE',
    name: '',
    status: 'DRAFT',
    startDate: null,
    endDate: null,
    conditions: [newCondition(1)],
  });
  const [loading, setLoading] = useState(ruleSetId != null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ruleSetId == null) return;
    let cancelled = false;
    setLoading(true);
    approvalAdminClient
      .ruleSet(ruleSetId)
      .then(detail => {
        if (cancelled) return;
        setForm({
          programId: detail.ruleSet.programId,
          name: detail.ruleSet.name,
          status: detail.ruleSet.status,
          startDate: detail.ruleSet.startDate,
          endDate: detail.ruleSet.endDate,
          // 저장 요청에는 이름 필드(userName·roleJobTitleName)가 없다
          conditions: detail.conditions.map(condition => ({
            sortOrder: condition.sortOrder,
            isExecutiveRequired: condition.isExecutiveRequired,
            lines: condition.lines,
            steps: condition.steps.map(step => ({
              stepOrder: step.stepOrder,
              stepAssignMode: step.stepAssignMode,
              userId: step.userId,
              roleJobTitleCode: step.roleJobTitleCode,
              stepLabel: step.stepLabel,
              isFinalDelegate: step.isFinalDelegate,
              isMandatory: step.isMandatory,
            })),
          })),
        });
      })
      .catch(error => {
        errorToast('조회 실패', error, '전결규정을 불러오지 못했습니다');
        // 불러오지 못한 채 열어두면 기본값(빈 조건·DRAFT)으로 덮어쓸 수 있어 닫는다
        if (!cancelled) onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ruleSetId]);

  const updateCondition = (index: number, apply: (condition: EditableCondition) => EditableCondition) =>
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) => (i === index ? apply(condition) : condition)),
    }));

  const fieldOptions = CONDITION_FIELDS[form.programId] ?? [];

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: '입력 확인', description: '규정명을 입력해주세요', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body: RuleSetSaveRequest = { ...form, name: form.name.trim() };
      if (ruleSetId == null) {
        await approvalAdminClient.createRuleSet(body);
        toast({ title: '등록 완료', description: '전결규정을 등록했습니다' });
      } else {
        await approvalAdminClient.updateRuleSet(ruleSetId, body);
        toast({ title: '수정 완료', description: '전결규정을 수정했습니다' });
      }
      onSaved();
    } catch (error) {
      // 중복 조건·미지원 필드·비활성 사용자·배정 이력 존재는 서버가 400 으로 막는다
      errorToast(ruleSetId == null ? '등록 실패' : '수정 실패', error, '전결규정을 저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className='sm:max-w-[900px] max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{ruleSetId == null ? '새 전결규정' : '전결규정 수정'}</DialogTitle>
          <DialogDescription>
            같은 그룹의 조건식은 AND, 다른 그룹은 OR 로 묶입니다. 조건식이 없으면 항상 매칭되는 기본 결재선입니다.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className='space-y-2 py-4'>
            <Skeleton className='h-8 w-full' />
            <Skeleton className='h-8 w-full' />
            <Skeleton className='h-32 w-full' />
          </div>
        ) : (
          <div className='space-y-4 py-2'>
            <div className='grid grid-cols-3 gap-3'>
              <div className='space-y-1'>
                <Label>프로그램</Label>
                <Select
                  value={form.programId}
                  onValueChange={value => setForm(prev => ({ ...prev, programId: value }))}
                  disabled={ruleSetId != null}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PROGRAM_LABEL).map(([code, name]) => (
                      <SelectItem key={code} value={code}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-1'>
                <Label htmlFor='rule-set-name'>규정명</Label>
                <Input
                  id='rule-set-name'
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder='예: 2026년 판매단가 전결규정'
                />
              </div>
              <div className='space-y-1'>
                <Label>상태</Label>
                <Select value={form.status} onValueChange={value => setForm(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RULE_STATUS_LABEL).map(([code, name]) => (
                      <SelectItem key={code} value={code}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-1'>
                <Label htmlFor='rule-set-start'>시작일</Label>
                <Input
                  id='rule-set-start'
                  type='date'
                  value={form.startDate ?? ''}
                  onChange={e => setForm(prev => ({ ...prev, startDate: textOrNull(e.target.value) }))}
                />
              </div>
              <div className='space-y-1'>
                <Label htmlFor='rule-set-end'>종료일</Label>
                <Input
                  id='rule-set-end'
                  type='date'
                  value={form.endDate ?? ''}
                  onChange={e => setForm(prev => ({ ...prev, endDate: textOrNull(e.target.value) }))}
                />
              </div>
            </div>

            <div className='space-y-3'>
              {form.conditions.map((condition, ci) => (
                <div key={ci} className='space-y-3 rounded-lg border p-3'>
                  <div className='flex items-center gap-3'>
                    <span className='font-medium'>조건 묶음 {ci + 1}</span>
                    <div className='flex items-center gap-2'>
                      <Label htmlFor={`condition-sort-${ci}`} className='text-sm text-muted-foreground'>
                        정렬
                      </Label>
                      <Input
                        id={`condition-sort-${ci}`}
                        type='number'
                        className='h-8 w-20'
                        value={condition.sortOrder}
                        onChange={e => updateCondition(ci, c => ({ ...c, sortOrder: Number(e.target.value) }))}
                      />
                    </div>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='ml-auto h-8 text-red-600'
                      onClick={() =>
                        setForm(prev => ({ ...prev, conditions: prev.conditions.filter((_, i) => i !== ci) }))
                      }
                    >
                      <Trash2 className='mr-1 h-4 w-4' />
                      묶음 삭제
                    </Button>
                  </div>

                  <div className='space-y-2'>
                    <div className='text-sm font-medium'>조건식</div>
                    {condition.lines.length === 0 && (
                      <p className='text-sm text-muted-foreground'>조건식이 없습니다 (항상 매칭되는 기본 결재선)</p>
                    )}
                    {condition.lines.map((line, li) => (
                      <div key={li} className='grid grid-cols-12 items-center gap-2'>
                        <Input
                          type='number'
                          className='col-span-1 h-8'
                          title='조건 그룹'
                          value={line.conditionGroup}
                          onChange={e =>
                            updateCondition(ci, c => ({
                              ...c,
                              lines: c.lines.map((item, i) =>
                                i === li ? { ...item, conditionGroup: Number(e.target.value) } : item
                              ),
                            }))
                          }
                        />
                        <Input
                          type='number'
                          className='col-span-1 h-8'
                          title='순서'
                          value={line.lineOrder}
                          onChange={e =>
                            updateCondition(ci, c => ({
                              ...c,
                              lines: c.lines.map((item, i) =>
                                i === li ? { ...item, lineOrder: Number(e.target.value) } : item
                              ),
                            }))
                          }
                        />
                        <div className='col-span-3'>
                          <Select
                            value={line.conditionField}
                            onValueChange={value =>
                              updateCondition(ci, c => ({
                                ...c,
                                lines: c.lines.map((item, i) => (i === li ? { ...item, conditionField: value } : item)),
                              }))
                            }
                          >
                            <SelectTrigger className='h-8'>
                              <SelectValue placeholder='조건 필드' />
                            </SelectTrigger>
                            <SelectContent>
                              {fieldOptions.map(option => (
                                <SelectItem key={option.code} value={option.code}>
                                  {option.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className='col-span-2'>
                          <Select
                            value={line.conditionOperator}
                            onValueChange={value =>
                              updateCondition(ci, c => ({
                                ...c,
                                lines: c.lines.map((item, i) =>
                                  i === li ? { ...item, conditionOperator: value } : item
                                ),
                              }))
                            }
                          >
                            <SelectTrigger className='h-8'>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {OPERATORS.map(operator => (
                                <SelectItem key={operator} value={operator}>
                                  {operator}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Input
                          className='col-span-2 h-8'
                          placeholder='값'
                          value={line.conditionValue1}
                          onChange={e =>
                            updateCondition(ci, c => ({
                              ...c,
                              lines: c.lines.map((item, i) =>
                                i === li ? { ...item, conditionValue1: e.target.value } : item
                              ),
                            }))
                          }
                        />
                        <Input
                          className='col-span-2 h-8'
                          placeholder='값2 (BETWEEN)'
                          value={line.conditionValue2 ?? ''}
                          onChange={e =>
                            updateCondition(ci, c => ({
                              ...c,
                              lines: c.lines.map((item, i) =>
                                i === li ? { ...item, conditionValue2: textOrNull(e.target.value) } : item
                              ),
                            }))
                          }
                        />
                        <Button
                          variant='ghost'
                          size='sm'
                          className='col-span-1 h-8 w-8 p-0 text-red-600'
                          onClick={() =>
                            updateCondition(ci, c => ({ ...c, lines: c.lines.filter((_, i) => i !== li) }))
                          }
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() =>
                        updateCondition(ci, c => ({
                          ...c,
                          lines: [...c.lines, newLine(form.programId, (c.lines.at(-1)?.conditionGroup ?? 1), c.lines.length + 1)],
                        }))
                      }
                    >
                      <Plus className='mr-1 h-4 w-4' />
                      조건식 추가
                    </Button>
                  </div>

                  <div className='space-y-2'>
                    <div className='text-sm font-medium'>결재 단계</div>
                    {condition.steps.map((step, si) => (
                      <div key={si} className='grid grid-cols-12 items-center gap-2'>
                        <Input
                          type='number'
                          className='col-span-1 h-8'
                          title='단계'
                          value={step.stepOrder}
                          onChange={e =>
                            updateCondition(ci, c => ({
                              ...c,
                              steps: c.steps.map((item, i) =>
                                i === si ? { ...item, stepOrder: Number(e.target.value) } : item
                              ),
                            }))
                          }
                        />
                        <div className='col-span-2'>
                          <Select
                            value={step.stepAssignMode}
                            onValueChange={value =>
                              updateCondition(ci, c => ({
                                ...c,
                                steps: c.steps.map((item, i) =>
                                  i === si
                                    ? {
                                        ...item,
                                        stepAssignMode: value === 'ORG_ROLE' ? 'ORG_ROLE' : 'FIXED',
                                        userId: value === 'ORG_ROLE' ? null : item.userId,
                                        roleJobTitleCode: value === 'ORG_ROLE' ? item.roleJobTitleCode : null,
                                      }
                                    : item
                                ),
                              }))
                            }
                          >
                            <SelectTrigger className='h-8'>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='FIXED'>지정 결재자</SelectItem>
                              <SelectItem value='ORG_ROLE'>조직 직책</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className='col-span-3'>
                          {step.stepAssignMode === 'FIXED' ? (
                            <Input
                              type='number'
                              className='h-8'
                              placeholder='사용자 id'
                              value={step.userId ?? ''}
                              onChange={e =>
                                updateCondition(ci, c => ({
                                  ...c,
                                  steps: c.steps.map((item, i) =>
                                    i === si ? { ...item, userId: numOrNull(e.target.value) } : item
                                  ),
                                }))
                              }
                            />
                          ) : (
                            <Select
                              value={step.roleJobTitleCode ?? ''}
                              onValueChange={value =>
                                updateCondition(ci, c => ({
                                  ...c,
                                  steps: c.steps.map((item, i) =>
                                    i === si ? { ...item, roleJobTitleCode: value } : item
                                  ),
                                }))
                              }
                            >
                              <SelectTrigger className='h-8'>
                                <SelectValue placeholder='직책 선택' />
                              </SelectTrigger>
                              <SelectContent>
                                {(jobTitles ?? []).map(jobTitle => (
                                  <SelectItem key={jobTitle.code} value={jobTitle.code}>
                                    {jobTitle.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <Input
                          className='col-span-3 h-8'
                          placeholder='단계 라벨 (예: 팀장)'
                          value={step.stepLabel ?? ''}
                          onChange={e =>
                            updateCondition(ci, c => ({
                              ...c,
                              steps: c.steps.map((item, i) =>
                                i === si ? { ...item, stepLabel: textOrNull(e.target.value) } : item
                              ),
                            }))
                          }
                        />
                        <label className='col-span-2 flex cursor-pointer items-center gap-2 text-sm'>
                          <Checkbox
                            checked={step.isFinalDelegate}
                            onCheckedChange={checked =>
                              updateCondition(ci, c => ({
                                ...c,
                                steps: c.steps.map((item, i) =>
                                  i === si ? { ...item, isFinalDelegate: checked === true } : item
                                ),
                              }))
                            }
                          />
                          <span>전결</span>
                        </label>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='col-span-1 h-8 w-8 p-0 text-red-600'
                          onClick={() =>
                            updateCondition(ci, c => ({ ...c, steps: c.steps.filter((_, i) => i !== si) }))
                          }
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() =>
                        updateCondition(ci, c => ({ ...c, steps: [...c.steps, newStep(c.steps.length + 1)] }))
                      }
                    >
                      <Plus className='mr-1 h-4 w-4' />
                      단계 추가
                    </Button>
                  </div>
                </div>
              ))}

              <Button
                variant='outline'
                onClick={() =>
                  setForm(prev => ({ ...prev, conditions: [...prev.conditions, newCondition(prev.conditions.length + 1)] }))
                }
              >
                <Plus className='mr-2 h-4 w-4' />
                조건 묶음 추가
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- 3. 위임

function DelegationsTab() {
  const { data, loading, refetch } = useApi(() => approvalAdminClient.delegations(), []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const delegations = data ?? [];

  const handleDelete = async (delegation: Delegation) => {
    if (!confirm(`${delegation.originalApproverName ?? delegation.originalApproverId} 위임을 취소하시겠습니까?`)) return;
    try {
      await approvalAdminClient.deleteDelegation(delegation.id);
      toast({ title: '취소 완료', description: '위임을 취소했습니다' });
      refetch();
    } catch (error) {
      errorToast('취소 실패', error, '위임을 취소하지 못했습니다');
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex justify-end'>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className='mr-2 h-4 w-4' />위임 등록
        </Button>
      </div>

      <div className='rounded-lg border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>원결재자</TableHead>
              <TableHead>대리자</TableHead>
              <TableHead className='w-40'>대상 프로그램</TableHead>
              <TableHead className='w-56'>기간</TableHead>
              <TableHead>사유</TableHead>
              <TableHead className='w-24'>등록구분</TableHead>
              <TableHead className='w-20 text-right'>작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <LoadingRows cols={7} />
            ) : delegations.length === 0 ? (
              <EmptyRow cols={7} message='등록된 위임이 없습니다' />
            ) : (
              delegations.map(delegation => (
                <TableRow key={delegation.id}>
                  <TableCell className='font-medium'>
                    {delegation.originalApproverName ?? delegation.originalApproverId}
                  </TableCell>
                  <TableCell>{delegation.delegateApproverName ?? delegation.delegateApproverId}</TableCell>
                  <TableCell>{programLabel(delegation.scopeProgramId)}</TableCell>
                  <TableCell className='text-muted-foreground'>
                    {delegation.startDate} ~ {delegation.endDate}
                  </TableCell>
                  <TableCell className='text-muted-foreground'>{delegation.reason || '-'}</TableCell>
                  <TableCell>
                    {delegation.sourceType === 'SELF' ? (
                      <Badge variant='secondary'>본인등록</Badge>
                    ) : (
                      <Badge variant='outline'>인사담당</Badge>
                    )}
                  </TableCell>
                  <TableCell className='text-right'>
                    {/* 본인이 등록한 위임은 관리자가 손대지 않는다. disabled 버튼은 포인터 이벤트를 먹으므로 title 은 감싼 span 에 둔다 */}
                    <span title={delegation.sourceType === 'SELF' ? '본인만 수정·취소' : undefined}>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-8 w-8 p-0 text-red-600'
                        disabled={delegation.sourceType === 'SELF'}
                        onClick={() => handleDelete(delegation)}
                      >
                        <Trash2 className='h-4 w-4' />
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {dialogOpen && (
        <DelegationDialog
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function DelegationDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [originalApproverId, setOriginalApproverId] = useState('');
  const [delegateApproverId, setDelegateApproverId] = useState('');
  const [scopeProgramId, setScopeProgramId] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const original = numOrNull(originalApproverId);
    const delegate = numOrNull(delegateApproverId);
    if (original == null || delegate == null || !startDate || !endDate) {
      toast({
        title: '입력 확인',
        description: '원결재자·대리자 사용자 id 와 기간은 필수입니다',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      await approvalAdminClient.createDelegation({
        originalApproverId: original,
        delegateApproverId: delegate,
        scopeProgramId: scopeProgramId === 'ALL' ? null : scopeProgramId,
        startDate,
        endDate,
        reason: textOrNull(reason),
      });
      toast({ title: '등록 완료', description: '위임을 등록했습니다' });
      onSaved();
    } catch (error) {
      // 기간 겹침·대리자 메뉴 권한 없음은 서버가 4xx 로 막는다
      errorToast('등록 실패', error, '위임을 등록하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle>위임 등록</DialogTitle>
          <DialogDescription>
            같은 결재자에게 기간이 겹치는 위임은 등록할 수 없고, 대리자는 해당 결재 메뉴 권한이 있어야 합니다.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-3 py-2'>
          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-1'>
              <Label htmlFor='delegation-original'>원결재자 사용자 id</Label>
              <Input
                id='delegation-original'
                type='number'
                value={originalApproverId}
                onChange={e => setOriginalApproverId(e.target.value)}
              />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='delegation-delegate'>대리자 사용자 id</Label>
              <Input
                id='delegation-delegate'
                type='number'
                value={delegateApproverId}
                onChange={e => setDelegateApproverId(e.target.value)}
              />
            </div>
          </div>
          <p className='text-sm text-muted-foreground'>사용자 id 는 사용자 관리 화면에서 확인해 직접 입력합니다.</p>

          <div className='space-y-1'>
            <Label>대상 프로그램</Label>
            <Select value={scopeProgramId} onValueChange={setScopeProgramId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ALL'>전체</SelectItem>
                {Object.entries(PROGRAM_LABEL).map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-1'>
              <Label htmlFor='delegation-start'>시작일</Label>
              <Input id='delegation-start' type='date' value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className='space-y-1'>
              <Label htmlFor='delegation-end'>종료일</Label>
              <Input id='delegation-end' type='date' value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='delegation-reason'>사유</Label>
            <Textarea
              id='delegation-reason'
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            등록
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- 4. 직책 코드

function JobTitlesTab() {
  const { data, loading, refetch } = useApi(() => approvalAdminClient.jobTitles(true), []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<JobTitle | null>(null);
  const jobTitles = data ?? [];

  const openDialog = (jobTitle: JobTitle | null) => {
    setEditing(jobTitle);
    setDialogOpen(true);
  };

  return (
    <div className='space-y-4'>
      <div className='flex justify-end'>
        <Button onClick={() => openDialog(null)}>
          <Plus className='mr-2 h-4 w-4' />새 직책
        </Button>
      </div>

      <div className='rounded-lg border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-40'>코드</TableHead>
              <TableHead>이름</TableHead>
              <TableHead className='w-44'>조회범위</TableHead>
              <TableHead className='w-20'>정렬</TableHead>
              <TableHead className='w-24'>사용여부</TableHead>
              <TableHead className='w-20 text-right'>작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <LoadingRows cols={6} />
            ) : jobTitles.length === 0 ? (
              <EmptyRow cols={6} message='등록된 직책 코드가 없습니다' />
            ) : (
              jobTitles.map(jobTitle => (
                <TableRow key={jobTitle.code}>
                  <TableCell className='font-mono'>{jobTitle.code}</TableCell>
                  <TableCell className='font-medium'>{jobTitle.name}</TableCell>
                  <TableCell className='text-muted-foreground'>{SEARCH_SCOPE_LABEL[jobTitle.searchScope]}</TableCell>
                  <TableCell>{jobTitle.sortOrder}</TableCell>
                  <TableCell>
                    {jobTitle.isActive ? <Badge variant='outline'>사용</Badge> : <Badge variant='secondary'>미사용</Badge>}
                  </TableCell>
                  <TableCell className='text-right'>
                    <Button variant='ghost' size='sm' className='h-8 w-8 p-0' onClick={() => openDialog(jobTitle)}>
                      <Pencil className='h-4 w-4' />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {dialogOpen && (
        <JobTitleDialog
          jobTitle={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function JobTitleDialog({
  jobTitle,
  onClose,
  onSaved,
}: {
  jobTitle: JobTitle | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(jobTitle?.code ?? '');
  const [name, setName] = useState(jobTitle?.name ?? '');
  const [sortOrder, setSortOrder] = useState(jobTitle?.sortOrder?.toString() ?? '1');
  const [isActive, setIsActive] = useState(jobTitle?.isActive ?? true);
  const [searchScope, setSearchScope] = useState<SearchScope>(jobTitle?.searchScope ?? 'ALL');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!code.trim() || !name.trim()) {
      toast({ title: '입력 확인', description: '코드와 이름은 필수입니다', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await approvalAdminClient.saveJobTitle({
        code: code.trim(),
        name: name.trim(),
        sortOrder: Number(sortOrder) || 0,
        isActive,
        searchScope,
      });
      toast({ title: '저장 완료', description: '직책 코드를 저장했습니다' });
      onSaved();
    } catch (error) {
      errorToast('저장 실패', error, '직책 코드를 저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className='sm:max-w-[420px]'>
        <DialogHeader>
          <DialogTitle>{jobTitle ? '직책 코드 수정' : '새 직책 코드'}</DialogTitle>
        </DialogHeader>

        <div className='grid gap-3 py-2'>
          <div className='space-y-1'>
            <Label htmlFor='job-title-code'>코드</Label>
            <Input
              id='job-title-code'
              value={code}
              onChange={e => setCode(e.target.value)}
              disabled={jobTitle != null}
              placeholder='예: TEAM_LEADER'
            />
          </div>
          <div className='space-y-1'>
            <Label htmlFor='job-title-name'>이름</Label>
            <Input id='job-title-name' value={name} onChange={e => setName(e.target.value)} placeholder='예: 팀장' />
          </div>
          <div className='space-y-1'>
            <Label htmlFor='job-title-sort'>정렬</Label>
            <Input
              id='job-title-sort'
              type='number'
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
            />
          </div>
          <div className='space-y-1'>
            <Label>조회범위</Label>
            <Select value={searchScope} onValueChange={value => setSearchScope(value as SearchScope)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(SEARCH_SCOPE_LABEL) as SearchScope[]).map(scope => (
                  <SelectItem key={scope} value={scope}>
                    {SEARCH_SCOPE_LABEL[scope]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              결재 조회조건의 사업장·부서 입력칸을 어디까지 열지 정합니다. 볼 수 있는 자료의 범위는 이 값과 무관합니다.
            </p>
          </div>
          <div className='flex items-center justify-between rounded-lg border p-3'>
            <Label htmlFor='job-title-active'>사용</Label>
            <Switch id='job-title-active' checked={isActive} onCheckedChange={setIsActive} />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            저장
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 조회범위 라벨. PRD 4.1 의 역할 분기를 직책 설정으로 옮긴 값이다 */
const SEARCH_SCOPE_LABEL: Record<SearchScope, string> = {
  SELF: '소속 사업장 고정',
  DEPT: '소속 부서 산하',
  ALL: '전체 선택 가능',
};

// ---------------------------------------------------------------- 5. 비활성 결재자

function PendingApproversTab() {
  const { data, loading } = useApi(() => approvalAdminClient.pendingApprovers(), []);
  const rows = data ?? [];

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        결재선은 재배정하지 않습니다. 기안자에게 자진취소 후 재기안을 안내하세요.
      </p>

      <div className='rounded-lg border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-44'>요청번호</TableHead>
              <TableHead className='w-40'>프로그램</TableHead>
              <TableHead className='w-24'>상태</TableHead>
              <TableHead className='w-16'>단계</TableHead>
              <TableHead>결재자</TableHead>
              <TableHead>기안자</TableHead>
              <TableHead className='w-40'>등록일시</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <LoadingRows cols={7} />
            ) : rows.length === 0 ? (
              <EmptyRow cols={7} message='비활성 사용자가 결재 대기 중인 건이 없습니다' />
            ) : (
              rows.map(row => (
                <TableRow key={`${row.requestId}-${row.stepOrder}-${row.approverId}`}>
                  <TableCell className='font-medium'>{row.documentNo}</TableCell>
                  <TableCell>{programLabel(row.programId)}</TableCell>
                  <TableCell>
                    <StatusBadge status={row.status} />
                  </TableCell>
                  <TableCell>{row.stepOrder}</TableCell>
                  <TableCell>
                    {row.approverName ?? '-'}
                    <span className='ml-1 text-muted-foreground'>({row.approverLoginId})</span>
                  </TableCell>
                  <TableCell>{row.drafterName ?? row.drafterId}</TableCell>
                  <TableCell className='text-muted-foreground'>{formatDateTime(row.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
